import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const isSpanish = i18n.language === 'es';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      setError(isSpanish ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError(isSpanish ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || (isSpanish ? 'Error al restablecer la contraseña' : 'Failed to reset password'));
      }
      
      setSuccess(true);
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              {isSpanish ? 'Enlace Inválido' : 'Invalid Link'}
            </h2>
            <p className="text-slate-600">
              {isSpanish 
                ? 'Este enlace de restablecimiento es inválido o ha expirado.' 
                : 'This reset link is invalid or has expired.'}
            </p>
            <Button onClick={() => navigate('/')} className="mt-4">
              {isSpanish ? 'Volver al Inicio' : 'Go to Home'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              {isSpanish ? '¡Contraseña Actualizada!' : 'Password Updated!'}
            </h2>
            <p className="text-slate-600">
              {isSpanish 
                ? 'Tu contraseña ha sido restablecida. Redirigiendo...' 
                : 'Your password has been reset. Redirecting...'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">
            {isSpanish ? 'Nueva Contraseña' : 'New Password'}
          </CardTitle>
          <p className="text-slate-500 mt-2">
            {isSpanish 
              ? 'Ingresa tu nueva contraseña' 
              : 'Enter your new password'}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder={isSpanish ? 'Nueva contraseña' : 'New password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder={isSpanish ? 'Confirmar contraseña' : 'Confirm password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full"
                data-testid="confirm-password-input"
              />
            </div>
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-cyan-500 hover:bg-cyan-600"
              data-testid="reset-password-btn"
            >
              {loading 
                ? (isSpanish ? 'Guardando...' : 'Saving...') 
                : (isSpanish ? 'Restablecer Contraseña' : 'Reset Password')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
